import { Alert, Breakpoint, Button, Card, CardActions, CardContent, CardHeader, CircularProgress, Container, Stack, Typography } from '@mui/material';
import { ComponentProps, FunctionComponent, PropsWithChildren, ReactElement } from 'react';
import { muiStyleSheet } from '../../util/Styles';
import { AccessibleStack } from '../Accessibility';
import { BackgroundLogo } from '../BackgroundLogo';
import { ScreenTitle } from '../ScreenTitle';

type NarrowProps = PropsWithChildren<{
	title?: string;
	stackProps?: ComponentProps<typeof Stack>;
}>;

export const NarrowAdminLayout: FunctionComponent<NarrowProps> = (props) => {
	return <Container maxWidth="sm">
		<ScreenTitle title={props.title} />
		<BackgroundLogo style={{ zIndex: -1 }} />
		<Stack spacing={3} role='main'
			{...props.stackProps}
			sx={{
				alignItems: 'center',
				justifyContent: 'center',
				minHeight: '100vh',
				...props.stackProps?.sx,
			}}>
			{props.title && <Typography variant="h2" gutterBottom>
				{props.title}
			</Typography>}
			{props.children}
		</Stack>
	</Container>;
};

type WideProps = NarrowProps & {
	/** If provided will be displayed as a loading message instead of the children */
	loading?: string;
	/** If provided will be displayed as an error message instead of the children */
	error?: string;
	width?: Breakpoint;
	actions?: ReactElement;
};

export const WideAdminLayout: FunctionComponent<WideProps> = (props) => {
	if (props.error) {
		return <Container maxWidth={props.width} sx={styles.screenCentred}>
			<ScreenTitle title={props.title} />
			<BackgroundLogo />
			<Card sx={styles.errorCard}>
				<CardHeader title='An error occurred' />
				<CardContent>
					<Alert severity="error">{props.error}</Alert>
				</CardContent>
				<CardActions>
					<Button onClick={() => window.location.reload()}>Reload page</Button>
				</CardActions>
			</Card>
		</Container>;
	} else if (props.loading) {
		return <Container sx={styles.screenCentred}>
			<ScreenTitle title={props.title} />
			<BackgroundLogo />
			<CircularProgress />
			<Typography sx={{ mt: 2 }}>{props.loading}</Typography>
		</Container>;
	} else {
		return <Container maxWidth={props.width} sx={{ marginY: 16 }}>
			<ScreenTitle title={props.title} />
			<BackgroundLogo style={{ zIndex: -1 }} />
			<AccessibleStack direction='row' sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
				<Typography variant="h2" gutterBottom>{props.title}</Typography>
				<Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
					{props.actions}
				</Stack>
			</AccessibleStack>
			<Stack spacing={3} role='main'
				{...props.stackProps}
			>
				{props.children}
			</Stack>
		</Container>;
	}
};

const styles = muiStyleSheet({
	screenCentred: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		height: '100vh',
	},
	errorCard: {
		width: '400px',
	}
});